import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Calendar,
  Shield,
  CheckCircle,
  Clock,
  XCircle,
  Ban,
  Edit,
  Key,
  Star,
  Truck,
  Package,
  Briefcase,
  Award
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Skeleton from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const UserProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  

  useEffect(() => {
    if (id) {
      fetchUserProfile();
    }
  }, [id]);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getUserById(id);
      setUser(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement du profil:', err);
      setError('Impossible de charger le profil utilisateur');
      toast({
        title: 'Erreur',
        description: 'Impossible de charger le profil utilisateur',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };


  const getStatusConfig = (status) => {
    const configs = {
      ACTIVE: { icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-500/10', label: 'Actif' },
      PENDING: { icon: Clock, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', label: 'En attente' },
      SUSPENDED: { icon: XCircle, color: 'text-amber-500', bgColor: 'bg-amber-500/10', label: 'Suspendu' },
      BANNED: { icon: Ban, color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'Banni' },
    };
    return configs[status] || configs.PENDING;
  };

  const getRoleConfig = (role) => {
    const configs = {
      ADMIN: { color: 'bg-teal-600/10 text-teal-600 border-teal-600/20', label: 'Admin', icon: Shield },
      CLIENT: { color: 'bg-amber-500/10 text-amber-600 border-amber-500/20', label: 'Client', icon: User },
      TRANSPORTEUR: { color: 'bg-teal-500/10 text-teal-500 border-teal-500/20', label: 'Transporteur', icon: Truck },
      AGENT: { color: 'bg-amber-600/10 text-amber-700 border-amber-600/20', label: 'Agent', icon: Briefcase },
    };
    return configs[role] || configs.CLIENT;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderProfileInfo = () => {
    if (!user) return null;

    const profile = user.profile_data;
    if (!profile) return null;

    switch (user.role) {
      case 'CLIENT':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Nom de l'entreprise</label>
                <p className="text-lg">{profile.company_name || 'Non spécifié'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Type de client</label>
                <p className="text-lg">{profile.type_client || 'Non spécifié'}</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-500">Adresse</label>
              <p className="text-lg">{profile.address || 'Non spécifiée'}</p>
            </div>
            
            {profile.domaine_activites && profile.domaine_activites.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Domaines d'activité</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.domaine_activites.map((domain, index) => (
                    <Badge key={index} variant="secondary">
                      {domain}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'TRANSPORTEUR':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Nom de l'organisation</label>
                <p className="text-lg">{profile.organisation_name || 'Non spécifié'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Type</label>
                <p className="text-lg">{profile.is_company ? 'Entreprise' : 'Indépendant'}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Numéro d'enregistrement</label>
                <p className="text-lg">{profile.registration_number || 'Non spécifié'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Informations fiscales</label>
                <p className="text-lg">{profile.tax_info || 'Non spécifiées'}</p>
              </div>
            </div>
            
            {profile.transport_domains && profile.transport_domains.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500">Domaines de transport</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.transport_domains.map((domain, index) => (
                    <Badge key={index} variant="secondary">
                      {domain}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Note moyenne</label>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="text-lg">{profile.rating_avg || '0.00'}/5.00</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Solde</label>
                <p className="text-lg font-semibold">{profile.balance || '0.00'} GNF</p>
              </div>
            </div>
          </div>
        );

      case 'AGENT':
        return (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Niveau de rôle</label>
              <p className="text-lg">{profile.role_level || 'Non spécifié'}</p>
            </div>
            
            {profile.notes && (
              <div>
                <label className="text-sm font-medium text-gray-500">Notes</label>
                <p className="text-lg">{profile.notes}</p>
              </div>
            )}
          </div>
        );

      default:
        return <p className="text-gray-500">Aucune information de profil disponible</p>;
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-10 w-10" />
            <Skeleton className="h-8 w-64" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-64 w-full" />
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !user) {
    return (
      <DashboardLayout sidebarLinks={adminSidebarLinks}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error || 'Utilisateur non trouvé'}</p>
            <Button onClick={() => navigate('/admin/users')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la liste
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = getStatusConfig(user.status);
  const roleConfig = getRoleConfig(user.role);

  return (
    <DashboardLayout sidebarLinks={adminSidebarLinks}>
      <Helmet>
        <title>Profil Utilisateur - {user.first_name} {user.last_name} | LogiConnect Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/users')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div className="flex items-center space-x-4">
              <Avatar className="h-16 w-16 border-2 border-primary">
                <AvatarImage 
                  src={user?.profile_photo || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.first_name}%20${user?.last_name}`} 
                  alt={`${user?.first_name} ${user?.last_name}`} 
                />
                <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-2xl font-bold">{user?.first_name} {user?.last_name}</h1>
                <p className="text-gray-600">{user?.email}</p>
                {(user?.logiconnect_id || user?.profile_data?.logiconnect_id) && (
                  <div className="mt-2">
                    <LogiConnectIdBadge 
                      id={user?.profile_data?.logiconnect_id || user?.logiconnect_id} 
                      showLabel={true}
                      size="sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations principales */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informations personnelles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Informations personnelles</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Prénom</label>
                    <p className="text-lg">{user.first_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nom</label>
                    <p className="text-lg">{user.last_name}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-lg">{user.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Téléphone</label>
                    <p className="text-lg">{user.phone_number || 'Non spécifié'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Rôle</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <roleConfig.icon className="w-4 h-4" />
                      <Badge className={roleConfig.color}>
                        {roleConfig.label}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Statut</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <statusConfig.icon className={`w-4 h-4 ${statusConfig.color}`} />
                      <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0`}>
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {user?.logiconnect_id && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">ID Utilisateur LogiConnect</label>
                      <div className="mt-1">
                        <LogiConnectIdBadge 
                          id={user.logiconnect_id} 
                          showLabel={false}
                          size="default"
                        />
                      </div>
                    </div>
                  )}
                  {user?.profile_data?.logiconnect_id && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">ID Profil LogiConnect</label>
                      <div className="mt-1">
                        <LogiConnectIdBadge 
                          id={user.profile_data.logiconnect_id} 
                          showLabel={false}
                          size="default"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date d'inscription</label>
                    <p className="text-lg">{formatDate(user.date_joined)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Dernière connexion</label>
                    <p className="text-lg">{formatDate(user.last_login)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Informations du profil */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Briefcase className="w-5 h-5" />
                  <span>Informations du profil</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderProfileInfo()}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions rapides */}
            <Card>
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button className="w-full justify-start" variant="outline">
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier le profil
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Key className="w-4 h-4 mr-2" />
                  Réinitialiser mot de passe
                </Button>
                <Button className="w-full justify-start" variant="outline">
                  <Shield className="w-4 h-4 mr-2" />
                  Gérer les permissions
                </Button>
                <Separator />
                <Button className="w-full justify-start" variant="destructive">
                  <Ban className="w-4 h-4 mr-2" />
                  Suspendre le compte
                </Button>
              </CardContent>
            </Card>

            {/* Statistiques */}
            <Card>
              <CardHeader>
                <CardTitle>Statistiques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Compte actif</span>
                  <Badge variant={user.is_active ? 'default' : 'destructive'}>
                    {user.is_active ? 'Oui' : 'Non'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Email vérifié</span>
                  <Badge variant={user.is_verified ? 'default' : 'secondary'}>
                    {user.is_verified ? 'Oui' : 'Non'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">ID Utilisateur</span>
                  <span className="text-sm font-mono">{user.id}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

    </DashboardLayout>
  );
};

export default UserProfile;
